import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { z } from 'zod';

import Checkbox from 'components/Checkbox/Checkbox';
import { cn } from 'components/lib/utils';
import UploadImage from 'components/UploadImage/UploadImage';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../components/ui/form';
import { Input } from '../../../../../../components/ui/input';
import { IBlockContentProps } from '../../../constants';
import {
  dailyBonusBlockSchema,
  pageFormSchema
} from '../../../validation-schema';
import { BlockSegmentsField } from '../Fields/BlockSegmentsField';

type DailyBonusBlockType = z.infer<typeof dailyBonusBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type DailyBonusFieldProps<T extends keyof DailyBonusBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: DailyBonusBlockType[T];
  };

export const DailyBonusForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}` as const;

  return (
    <div
      className={
        'flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border'
      }
    >
      <FormField
        control={control}
        name={`${basePath}.backgroundImage`}
        render={({
          field: { onChange, value }
        }: {
          field: DailyBonusFieldProps<'backgroundImage'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Default Background Image"
                description={`Add a background image to your daily bonus block.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  errors[`${basePath}.backgroundImage` as keyof typeof errors]
                    ?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-BackgroundImage"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.backgroundImageMobile`}
        render={({
          field: { onChange, value }
        }: {
          field: DailyBonusFieldProps<'backgroundImageMobile'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Mobile Background Image"
                description={`Add a mobile background image to your daily bonus block.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  errors[
                    `${basePath}.backgroundImageMobile` as keyof typeof errors
                  ]?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-BackgroundImageMobile"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.redirectUrl`}
        render={({ field }: { field: DailyBonusFieldProps<'redirectUrl'> }) => (
          <FormItem>
            <FormLabel>Redirect URL</FormLabel>
            <FormControl>
              <Input placeholder="Enter redirect URL" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.isStoreRedirect`}
        render={({
          field
        }: {
          field: DailyBonusFieldProps<'isStoreRedirect'>;
        }) => (
          <FormItem>
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                label={'External webstore Redirect'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <BlockSegmentsField basePath={basePath} />
    </div>
  );
};
